/**
 * ポイント一覧APIのCSVをPointのリストに戻す
 *
 * 列名は応答の1行目にあるものだけを使う
 * storelocatorに持たせると、フィールド設定の更新からAPIのエッジキャッシュが切れるまでの間だけ列がずれ、絞り込み結果だけが静かに変わる
 *
 * 応答の取得は api.ts、形式の指定は pointsApiFormat.ts が持つ
 */

import { parseCsv } from '@/utils/csv.ts'

/**
 * 数値へ戻す予約フィールド
 *
 * JSON形式ではDBのカラム型（AutoField・FloatField）がそのままnumberで降りるので、CSVでも数値に戻して形を揃える
 * 追加フィールドを文字列のままにするのと非対称だが、JSON形式で降りてくる型が違う（buildCellPlansを見る）
 *
 * Point.idは Array.includes の厳密比較に使われるので、文字列のままだと絞り込みが静かに壊れる
 * ここに無い予約フィールド（key・name・address・geo_hash）は文字列のまま入れる
 */
const NUMBER_COLUMNS = ['id', 'latitude', 'longitude']

/**
 * markersの列に付くプレフィックス
 *
 * markersは {言語コード: マーカーid} なので1列に収まらず、バックエンドが markers.ja、markers.en のように言語ごとの列へ開く
 * プレフィックスの後ろがそのまま言語コードになる
 */
const MARKERS_COLUMN_PREFIX = 'markers.'

/**
 * 追加フィールドの列に付くプレフィックス
 *
 * 追加フィールドのキーはユーザーが決めるので、予約フィールドと同名のものが作られ得る
 * バックエンドが名前空間を分けているので、プレフィックスの有無だけで予約フィールドと切り分けられる
 */
const EXTRA_FIELDS_COLUMN_PREFIX = 'extra_fields.'

/**
 * セルの値をPointのどこへ入れるか
 *
 * 予約フィールドだけ数値と文字列に分かれる
 * JSON形式ではDBのカラム型がそのまま降りてくるので、そこに揃えるため（NUMBER_COLUMNSを見る）
 *
 * バックエンドの points_csv._CellPlanSource に対応する
 */
const CELL_TARGET = {
  /**
   * point[列名] に数値で入れる（id・latitude・longitude）
   */
  RESERVED_NUMBER: 'reservedNumber',

  /**
   * point[列名] に文字列で入れる（key・name・address・geo_hash）
   */
  RESERVED_STRING: 'reservedString',

  /**
   * point.markers[言語コード] に入れる
   */
  MARKER: 'marker',

  /**
   * point.extra_fields[キー] に入れる
   */
  EXTRA_FIELD: 'extraField',
} as const

/**
 * CELL_TARGET が取る値
 */
type CellTarget = typeof CELL_TARGET[keyof typeof CELL_TARGET]

/**
 * 列ごとのセルの入れ先
 *
 * 列名からの判定は列ごとに一度だけ済ませ、行ごとにやり直さない
 * バックエンドの points_csv._build_cell_plans と同じ考え方
 */
interface CellPlan {
  /**
   * 入れ先のキー
   * markersの列は言語コードになる
   */
  key: string
  target: CellTarget
}

export class PointsCsvFormatError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PointsCsvFormatError'
  }
}

/**
 * CSVの応答をPointのリストに戻す
 *
 * 1行目を列名として読み、2行目以降を値として読む
 *
 * 空セルの扱いは列の種類で変える
 * 予約フィールドはキーを残し（数値はnull、文字列は空文字）、追加フィールドとmarkersはキーを作らない
 * JSON形式が空値の追加フィールドをキーごと落としているので、空文字を入れると絞り込みの未設定判定が変わる
 *
 * @param csv ヘッダ行付きのCSV
 * @returns Pointの配列。JSON形式の応答のitemsと同じ形になる
 * @throws PointsCsvFormatError 応答が文字列でない、ヘッダ行が無い、行の列数が列名と合わない、数値の列が数値でない
 * @throws CsvParseError クォートが閉じていない、クォートの後に値が続く
 */
export function restorePointsFromCsv(csv: unknown): Point[] {
  if (typeof csv !== 'string') {
    throw new PointsCsvFormatError(`response is not a string: ${typeof csv}`)
  }

  const rows = parseCsv(csv)

  // 1行目が列名
  const columns = rows.shift()
  if (!columns) {
    throw new PointsCsvFormatError('header row is missing')
  }

  // 列ごとの入れ先を先に決める
  const plans = buildCellPlans(columns)
  const hasMarkerColumn = plans.some(plan => plan.target === CELL_TARGET.MARKER)

  return rows.map((row, rowIndex) => {
    try {
      return restorePoint(plans, row, hasMarkerColumn)
    } catch (error) {
      if (error instanceof PointsCsvFormatError) {
        // 応答をそのまま開いて突き合わせられるよう、ヘッダ行を含めた1始まりの行番号にする
        throw new PointsCsvFormatError(`${error.message} at row ${rowIndex + 2}`)
      }

      throw error
    }
  })
}

/**
 * 列ごとに、セルの値をPointのどこへ入れるかを決める
 *
 * 追加フィールドは Map.fields の type（number・date・datetimeなど）を見ず、すべて文字列で入れる
 * 型の解釈は値を使う直前に行う設計で、format.ts の formatExtraFieldValue と絞り込みが storelocator.fields から type を引いている
 * ここで数値やDateに変換すると、Filters.ts が値に .trim() を呼ぶ経路と Point.extra_fields の型（string | null）に合わなくなる
 */
function buildCellPlans(columns: string[]): CellPlan[] {
  return columns.map((column): CellPlan => {
    // プレフィックスの付いた列だけがmarkersと追加フィールドで、残りは予約フィールドになる
    if (column.startsWith(MARKERS_COLUMN_PREFIX)) {
      // markers.ja → point.markers.ja
      return { key: column.slice(MARKERS_COLUMN_PREFIX.length), target: CELL_TARGET.MARKER }
    }

    if (column.startsWith(EXTRA_FIELDS_COLUMN_PREFIX)) {
      // extra_fields.Tel → point.extra_fields.Tel
      return { key: column.slice(EXTRA_FIELDS_COLUMN_PREFIX.length), target: CELL_TARGET.EXTRA_FIELD }
    }

    if (NUMBER_COLUMNS.includes(column)) {
      // id → point.id（数値に戻す）
      return { key: column, target: CELL_TARGET.RESERVED_NUMBER }
    }

    // name → point.name（文字列のまま）
    return { key: column, target: CELL_TARGET.RESERVED_STRING }
  })
}

/**
 * 1行をPointに戻す
 *
 * @throws PointsCsvFormatError 行の列数が列名と合わない、数値の列が数値でない
 */
function restorePoint(plans: CellPlan[], row: string[], hasMarkerColumn: boolean): Point {
  if (row.length !== plans.length) {
    throw new PointsCsvFormatError(`column count mismatch: ${row.length} != ${plans.length}`)
  }

  const point: { [key: string]: unknown } = {}
  const extraFields: { [key: string]: string } = {}
  const markers: { [key: string]: string } = {}

  plans.forEach(({ key, target }, columnIndex) => {
    const cell = row[columnIndex]

    if (target === CELL_TARGET.RESERVED_NUMBER) {
      point[key] = restoreNumberCell(key, cell)
      return
    }

    if (target === CELL_TARGET.RESERVED_STRING) {
      point[key] = cell
      return
    }

    // 追加フィールドとmarkersは空セルでキーを作らない
    if (cell === '') {
      return
    }

    if (target === CELL_TARGET.MARKER) {
      markers[key] = cell
      return
    }

    extraFields[key] = cell
  })

  point.extra_fields = extraFields

  // markersの列が無いマップでは、JSON形式でもmarkersキーが付かない
  if (hasMarkerColumn) {
    point.markers = markers
  }

  return point as unknown as Point
}

/**
 * 数値の列のセルを数値に戻す
 *
 * 空セルはnullにする
 * Point.latitudeはDB側でnullを許しているので、JSON形式の応答でもnullが入る
 *
 * @throws PointsCsvFormatError 数値として読めない
 */
function restoreNumberCell(key: string, cell: string): number | null {
  if (cell === '') {
    return null
  }

  const value = Number(cell)
  if (Number.isNaN(value)) {
    throw new PointsCsvFormatError(`${key} is not a number: ${cell}`)
  }

  return value
}
